import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInstagram,
  faDiscord,
  faYoutube,
  faTiktok,
} from '@fortawesome/free-brands-svg-icons';
import {
  faFaceSmile,
  faPaperPlane,
  faHeadset,
} from '@fortawesome/free-solid-svg-icons';
import logo from '@Assets/images/logo-80.png';
import { socialLinks } from 'src/config/socialLinks';
import { SURVEY_URL, CONTACT_EMAIL } from 'src/config/links';
import { trackButtonClick } from '../services/telemetry';

const columnTitleClass = 'text-lg font-normal text-[#afafaf]';
const linkClass =
  'text-base text-white transition-colors hover:text-yellow-400';
const socialIconClass = `
  flex h-8 w-8 items-center justify-center text-white transition-colors
  hover:text-yellow-400
`;

function Footer() {
  return (
    <footer
      className={`
        mt-auto bg-[#323232] px-6 pt-14 pb-10 font-[Outfit] text-white
        sm:px-10
        lg:px-20
      `}
    >
      <div
        className={`
          mx-auto flex max-w-6xl flex-col gap-12
          lg:flex-row lg:items-start lg:justify-between
        `}
      >
        {/* Brand + socials */}
        <div className="flex flex-col items-center gap-8 lg:items-start">
          <Link
            to="/"
            onClick={async () => await trackButtonClick('Footer', 'Logo Click')}
            className="flex items-center gap-1"
          >
            <img src={logo} alt="PineappleMusicLab" className="h-12 w-12" />
            <span className="text-2xl font-medium">PineappleMusicLab</span>
          </Link>
          <div className="flex items-center gap-8 lg:gap-10">
            <a
              href={socialLinks.youtube}
              onClick={async () =>
                await trackButtonClick('Footer', 'Connect: YouTube')
              }
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className={socialIconClass}
            >
              <FontAwesomeIcon icon={faYoutube} size="xl" />
            </a>
            <a
              href={socialLinks.tiktok}
              onClick={async () =>
                await trackButtonClick('Footer', 'Connect: TikTok')
              }
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className={socialIconClass}
            >
              <FontAwesomeIcon icon={faTiktok} size="xl" />
            </a>
            <a
              href={socialLinks.instagram}
              onClick={async () =>
                await trackButtonClick('Footer', 'Connect: Instagram')
              }
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className={socialIconClass}
            >
              <FontAwesomeIcon icon={faInstagram} size="xl" />
            </a>
            <a
              href={socialLinks.discordServer}
              onClick={async () =>
                await trackButtonClick('Footer', 'Connect: Discord')
              }
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Discord"
              className={socialIconClass}
            >
              <FontAwesomeIcon icon={faDiscord} size="xl" />
            </a>
          </div>
        </div>

        {/* Link columns */}
        <div
          className={`
            grid grid-cols-1 gap-10 text-center
            sm:grid-cols-3 sm:text-left
            lg:gap-24
          `}
        >
          {/* Documentation */}
          <div className="flex flex-col items-center gap-5 sm:items-start">
            <p className={columnTitleClass}>Documentation</p>
            <nav className="flex flex-col items-center gap-3 sm:items-start">
              <Link
                to="/tutorial"
                onClick={async () =>
                  await trackButtonClick('Footer', 'Documentation: Tutorial')
                }
                className={linkClass}
              >
                Tutorial
              </Link>
              <Link
                to="/business-api"
                onClick={async () =>
                  await trackButtonClick('Footer', 'Documentation: API Docs')
                }
                className={linkClass}
              >
                API Docs
              </Link>
              <Link
                to="/pricing"
                onClick={async () =>
                  await trackButtonClick('Footer', 'Documentation: Pricing')
                }
                className={linkClass}
              >
                Pricing
              </Link>
            </nav>
          </div>

          {/* Terms and policies */}
          <div className="flex flex-col items-center gap-5 sm:items-start">
            <p className={columnTitleClass}>Terms and policies</p>
            <nav className="flex flex-col items-center gap-3 sm:items-start">
              <Link
                to="/privacy-policy"
                onClick={async () =>
                  await trackButtonClick('Footer', 'Legal: Privacy Policy')
                }
                className={linkClass}
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                onClick={async () =>
                  await trackButtonClick('Footer', 'Legal: Terms of Service')
                }
                className={linkClass}
              >
                Terms of service
              </Link>
            </nav>
          </div>

          {/* Contact us */}
          <div className="flex flex-col items-center gap-5 sm:items-start">
            <p className={columnTitleClass}>Contact us</p>
            <nav className="flex flex-col items-center gap-3 sm:items-start">
              <Link
                to="/support"
                onClick={async () =>
                  await trackButtonClick('Footer', 'Contact: Support')
                }
                className={`${linkClass} flex items-center gap-2`}
              >
                <FontAwesomeIcon icon={faHeadset} className="text-[#afafaf]" />
                Support
              </Link>
              <a
                href={SURVEY_URL}
                onClick={async () =>
                  await trackButtonClick('Footer', 'Contact: Survey')
                }
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkClass} flex items-center gap-2`}
              >
                <FontAwesomeIcon
                  icon={faFaceSmile}
                  className="text-[#afafaf]"
                />
                1 min survey - help us improve
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                onClick={async () =>
                  await trackButtonClick('Footer', 'Contact: Email')
                }
                className={`${linkClass} flex items-center gap-2`}
              >
                <FontAwesomeIcon
                  icon={faPaperPlane}
                  className="text-[#afafaf]"
                />
                {CONTACT_EMAIL}
              </a>
            </nav>
          </div>
        </div>
      </div>

      <p className="mt-12 text-center text-base text-[#afafaf]">
        &copy; {new Date().getFullYear()} PineappleMusicLab. All rights
        reserved.
      </p>
    </footer>
  );
}

export { Footer };
